import React, { useState } from "react";
import { Dialog, Slide, Tooltip } from "@mui/material";
import {
	formatDueDate,
	formatINR,
	formatMonthLabel,
	formatUSD,
	formatCoins,
} from "./revenueSummaryUtils";

const Transition = React.forwardRef(function Transition(props, ref) {
	return <Slide direction="up" ref={ref} {...props} />;
});

const formatFxRate = (rate) => {
	if (rate == null || rate === 0) return "—";
	return Number(rate).toLocaleString("en-IN", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 4,
	});
};

const VOUCHERS_TOOLTIP =
	"Includes voucher withdrawals from all connected platforms (Prorewards, SurveyBucks, TaskPe, etc.)";

const formatPublisherCount = (publisher) => {
	if (publisher.isExternal && (publisher.count ?? 0) === 0) return "—";
	return (publisher.count ?? 0).toLocaleString();
};

const InfoTooltip = ({ title }) => (
	<Tooltip title={title} arrow placement="top">
		<span className="rs-info-icon" onClick={(e) => e.stopPropagation()}>i</span>
	</Tooltip>
);

const WithdrawalCategoryRows = ({
	label,
	sublabel,
	tooltip,
	total,
	byPublisher,
	fallbackValueINR,
	expanded,
	onToggle,
}) => {
	const publishers = Array.isArray(byPublisher) ? byPublisher : [];
	const hasChildren = publishers.length > 0;

	return (
		<>
			<tr
				className={`rs-withdrawal-row ${hasChildren ? "" : "rs-withdrawal-row--default"} ${expanded && hasChildren ? "rs-withdrawal-row--expanded" : ""}`}
				onClick={() => hasChildren && onToggle()}
			>
				<td className="rs-cell-outflow-sm">
					<div className="rs-breakdown-inline rs-breakdown-inline--left">
						<span className="rs-withdrawal-label-wrap">
							{hasChildren && (
								<span className="rs-expand-icon">{expanded ? "▼" : "▶"}</span>
							)}
							{label}
							{tooltip && <InfoTooltip title={tooltip} />}
						</span>
						{sublabel && <div className="rs-withdrawal-sublabel">{sublabel}</div>}
					</div>
				</td>
				<td className="rs-cell-outflow">{formatINR(total?.valueINR ?? fallbackValueINR)}</td>
				<td>{formatCoins(total?.coins)}</td>
				<td>{(total?.count ?? 0).toLocaleString()}</td>
			</tr>

			{expanded && publishers.map((publisher) => (
				<tr key={publisher.publisherId || publisher.publisherName}>
					<td className="rs-cell-publisher">{publisher.publisherName || "—"}</td>
					<td className="rs-cell-outflow-sm">{formatINR(publisher.valueINR)}</td>
					<td>{formatCoins(publisher.coins)}</td>
					<td>{formatPublisherCount(publisher)}</td>
				</tr>
			))}
		</>
	);
};

const MoneyOutDetailsModal = ({ open, onClose, withdrawalDetails, withdrawals }) => {
	const [upiExpanded, setUpiExpanded] = useState(true);
	const [vouchersExpanded, setVouchersExpanded] = useState(true);

	const upiDetail = withdrawalDetails?.upi || {};
	const voucherDetail = withdrawalDetails?.vouchers || {};
	const upiTotal = upiDetail.total || {};
	const voucherTotal = voucherDetail.total || {};
	const upiPublishers = Array.isArray(upiDetail.byPublisher) ? upiDetail.byPublisher : [];
	const voucherPublishers = Array.isArray(voucherDetail.byPublisher) ? voucherDetail.byPublisher : [];
	const resolvedUpiTotal = upiTotal.valueINR != null
		? upiTotal
		: { valueINR: upiDetail.valueINR, coins: upiDetail.coins, count: upiDetail.count };

	return (
		<Dialog open={open} onClose={onClose} TransitionComponent={Transition} maxWidth="md" fullWidth PaperProps={{ className: "rs-page" }}>
			<div className="dialog-content-v4 rs-modal-body rs-page">
				<div className="rs-modal-header">
					<h3 className="rs-modal-title">Money Out — Withdrawal Details</h3>
					<button type="button" onClick={onClose} className="rs-modal-close">Close</button>
				</div>
				<p className="rs-modal-desc rs-modal-desc--muted">
					UPI / IMPS and voucher payouts grouped by platform.
				</p>

				<div className="table-container-v4">
					<table className="data-table-v4 rs-modal-table">
						<thead>
							<tr>
								<th>Method / Platform</th>
								<th>Value (INR)</th>
								<th>Coins</th>
								<th>Qty / Txns</th>
							</tr>
						</thead>
						<tbody>
							<WithdrawalCategoryRows
								label={upiDetail.label || "UPI / IMPS"}
								sublabel="Prorewards + SurveyBucks"
								total={resolvedUpiTotal}
								byPublisher={upiPublishers}
								fallbackValueINR={withdrawals?.upi}
								expanded={upiExpanded}
								onToggle={() => setUpiExpanded((prev) => !prev)}
							/>
							<WithdrawalCategoryRows
								label="Vouchers"
								sublabel="All connected platforms"
								tooltip={VOUCHERS_TOOLTIP}
								total={voucherTotal}
								byPublisher={voucherPublishers}
								fallbackValueINR={withdrawals?.vouchers}
								expanded={vouchersExpanded}
								onToggle={() => setVouchersExpanded((prev) => !prev)}
							/>
						</tbody>
						<tfoot>
							<tr className="rs-table-foot">
								<td className="rs-table-foot-label">Total Withdrawals</td>
								<td className="rs-cell-outflow rs-table-foot-label">{formatINR(withdrawals?.total)}</td>
								<td colSpan={2} />
							</tr>
						</tfoot>
					</table>
				</div>
			</div>
		</Dialog>
	);
};

const PartialPaymentBadge = () => (
	<span className="rs-partial-badge">Partial Payment</span>
);

const formatPartnerName = (name) => {
	if (!name) return "—";
	return name
		.split(/\s+/)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(" ");
};

const getRowGstINR = (row) =>
	row.gstAmountINR ?? row.gstINR ?? row.amounts?.gstAmountINR ?? row.amounts?.gstINR ?? 0;

const getRowTdsINR = (row) =>
	row.tdsAmountINR ?? row.tdsINR ?? row.amounts?.tdsAmountINR ?? row.amounts?.tdsINR ?? 0;

const getRowGrossSentUSD = (row) =>
	row.grossSentUSD ?? row.grossSent ?? row.amounts?.grossSentUSD ?? row.amounts?.grossUSD ?? 0;

const getRowTransferChargesUSD = (row) =>
	row.transferChargesUSD ?? row.amounts?.transferChargesUSD ?? 0;

const getRowNetReceivedUSD = (row) => {
	const net = row.netReceivedUSD;
	if (net != null) return net;
	const gross = getRowGrossSentUSD(row);
	const charges = getRowTransferChargesUSD(row);
	if (gross || charges) return gross - charges;
	return 0;
};

const getRowFxRate = (row) => row.effectiveFxRate ?? row.amounts?.effectiveFxRate ?? 0;

const getRowNetReceivedINRFromUsd = (row) => {
	if (row.netReceivedINR != null) return row.netReceivedINR;
	const netUsd = getRowNetReceivedUSD(row);
	const rate = getRowFxRate(row);
	if (netUsd && rate) return Number((netUsd * rate).toFixed(2));
	return null;
};

const getRowNetINR = (row) => {
	if (row.billingType === "USD") return getRowNetReceivedINRFromUsd(row);
	return row.netReceivedINR ?? null;
};

const calculateBaseINR = (netReceivedINR, gstINR, tdsINR, fallback) => {
	const net = netReceivedINR ?? 0;
	const gst = gstINR ?? 0;
	const tds = tdsINR ?? 0;
	if (net || gst || tds) return net + tds - gst;
	return fallback ?? null;
};

const AmountBifurcation = ({ lines, compact = false, variant = "inr" }) => (
	<div className={`rs-amount-block ${compact ? "rs-amount-block--compact" : ""} ${variant === "usd" ? "rs-amount-block--usd" : ""}`}>
		{lines.map(({ label, value, emphasis, muted }) => (
			<div
				key={label}
				className={`rs-amount-line ${emphasis ? "rs-amount-line--emphasis" : ""} ${emphasis && variant === "usd" ? "rs-amount-line--emphasis-usd" : ""} ${muted ? "rs-amount-line--muted" : ""}`}
			>
				<span>{label}</span>
				<span className="rs-amount-line-value">{value}</span>
			</div>
		))}
	</div>
);

const InrRowBreakdown = ({ row }) => {
	const gst = getRowGstINR(row);
	const tds = getRowTdsINR(row);
	const received = row.netReceivedINR ?? 0;
	const base = calculateBaseINR(
		received,
		gst,
		tds,
		row.amounts?.grossINR ?? row.amounts?.grossSentINR ?? row.grossSent
	);

	const lines = [
		{ label: "Received", value: formatINR(received) },
		{ label: "− GST", value: formatINR(gst), muted: true },
		{ label: "+ TDS", value: formatINR(tds), muted: true },
		{ label: "Base", value: formatINR(base), emphasis: true },
	];

	return <AmountBifurcation lines={lines} compact />;
};

const UsdRowBreakdown = ({ row }) => {
	const released = getRowGrossSentUSD(row);
	const charges = getRowTransferChargesUSD(row);
	const netUsd = getRowNetReceivedUSD(row);
	const rate = getRowFxRate(row);
	const netInr = getRowNetReceivedINRFromUsd(row);

	const lines = [
		{ label: "Released", value: formatUSD(released) },
		{ label: "− Charges", value: formatUSD(charges), muted: true },
		{ label: "Net (USD)", value: formatUSD(netUsd) },
		{ label: "× Rate", value: formatFxRate(rate), muted: true },
		{ label: "Net (INR)", value: formatINR(netInr), emphasis: true },
	];

	return <AmountBifurcation lines={lines} compact variant="usd" />;
};

const PaymentRowBreakdown = ({ row }) =>
	row.billingType === "USD" ? <UsdRowBreakdown row={row} /> : <InrRowBreakdown row={row} />;

const BreakdownSummary = ({ title, variant, lines }) => (
	<div className="rs-breakdown-summary">
		<div className={`rs-breakdown-summary-title rs-breakdown-summary-title--${variant}`}>{title}</div>
		<AmountBifurcation lines={lines} />
	</div>
);

const MoneyInDetailsModal = ({ open, onClose, receivedDetails, receivedBreakdown, received }) => {
	const inrRows = receivedDetails.filter((r) => r.billingType === "INR");
	const usdRows = receivedDetails.filter((r) => r.billingType === "USD");
	const sortedRows = [...receivedDetails].sort((a, b) => {
		const partnerCmp = (a.partnerName || "").localeCompare(b.partnerName || "", undefined, { sensitivity: "base" });
		if (partnerCmp !== 0) return partnerCmp;
		const dateCmp = (b.receivedDate || "").localeCompare(a.receivedDate || "");
		if (dateCmp !== 0) return dateCmp;
		return (a.invoiceNumber || "").localeCompare(b.invoiceNumber || "", undefined, { sensitivity: "base" });
	});

	const inrSummary = receivedBreakdown?.inr || {};
	const usdSummary = receivedBreakdown?.usd || {};
	const totalNet = receivedBreakdown?.totalNetReceivedINR ?? received?.total;

	const inrNetReceived = inrSummary.netReceivedINR ?? received?.fromINRPartners ?? 0;
	const inrGst = inrSummary.gstINR ?? 0;
	const inrTds = inrSummary.tdsINR ?? 0;
	const inrBaseAmount = calculateBaseINR(
		inrNetReceived,
		inrGst,
		inrTds,
		inrSummary.grossSentINR ?? inrSummary.grossINR
	);

	const inrSummaryLines = [
		{ label: "Received in Bank", value: formatINR(inrNetReceived), emphasis: true },
		{ label: "− GST", value: formatINR(inrGst), muted: true },
		{ label: "+ TDS", value: formatINR(inrTds), muted: true },
		{ label: "Base Amount", value: formatINR(inrBaseAmount), emphasis: true },
	];

	const usdSummaryLines = [
		{ label: "Invoiced", value: formatUSD(usdSummary.grossUSD) },
		{ label: "Released", value: formatUSD(usdSummary.grossSentUSD) },
		{ label: "− Charges", value: formatUSD(usdSummary.transferChargesUSD) },
		{ label: "Net (USD)", value: formatUSD(usdSummary.netReceivedUSD), emphasis: true },
		{ label: "× Rate", value: formatFxRate(usdSummary.avgEffectiveFxRate) },
		{ label: "Net (INR)", value: formatINR(usdSummary.netReceivedINR ?? received?.fromUSDPartners), emphasis: true },
	];

	return (
		<Dialog open={open} onClose={onClose} TransitionComponent={Transition} maxWidth="lg" fullWidth PaperProps={{ className: "rs-page" }}>
			<div className="dialog-content-v4 rs-modal-body rs-page">
				<div className="rs-modal-header">
					<h3 className="rs-modal-title">Money In — Payment Details</h3>
					<button type="button" onClick={onClose} className="rs-modal-close">Close</button>
				</div>
				<p className="rs-modal-desc">
					Each row is a payment received in the bank during the selected month. Invoice month and received date may differ.
					INR partners: received + TDS − GST = base. USD partners: released − charges, converted at FX rate.
				</p>

				{receivedDetails.length === 0 ? (
					<div className="rs-empty rs-empty--sm">No payments received in this month.</div>
				) : (
					<>
						<div className="rs-money-in-summary">
							<div className="rs-money-in-summary-title">Summary</div>
							<div className="rs-summary-cards-row">
								{(inrSummary.netReceivedINR != null || inrRows.length > 0) && (
									<BreakdownSummary title="From INR Partners" variant="inr" lines={inrSummaryLines} />
								)}
								{(usdSummary.netReceivedINR != null || usdRows.length > 0) && (
									<BreakdownSummary title="From USD Partners (converted)" variant="usd" lines={usdSummaryLines} />
								)}
							</div>
							<div className="rs-total-money-in">
								<span>Total Money In</span>
								<span>{formatINR(totalNet)}</span>
							</div>
						</div>

						<div className="table-container-v4">
							<table className="data-table-v4 rs-modal-table rs-modal-table--sm rs-modal-table--payments">
								<thead>
									<tr>
										<th>Partner</th>
										<th>Invoice #</th>
										<th className="rs-col-date">Revenue Month</th>
										<th className="rs-col-date">Received Date</th>
										<th className="rs-col-breakdown">Payment Breakdown</th>
										<th className="rs-col-net">Net (INR)</th>
										<th>Partial</th>
									</tr>
								</thead>
								<tbody>
									{sortedRows.map((row) => {
										const rowKey = row.paymentId || `${row.invoiceId || row.invoiceNumber}-${row.receivedDate}-${row.billingType}`;
										return (
											<tr key={rowKey}>
												<td className="rs-col-partner rs-col-partner--left">
													{formatPartnerName(row.partnerName)}
												</td>
												<td className="rs-cell-muted">{row.invoiceNumber || "—"}</td>
												<td className="rs-col-date">
													{formatMonthLabel(row.invoiceMonth) || row.invoiceMonth || "—"}
												</td>
												<td className="rs-col-date">{formatDueDate(row.receivedDate)}</td>
												<td className="rs-col-breakdown">
													<PaymentRowBreakdown row={row} />
												</td>
												<td className="rs-cell-net-inr">{formatINR(getRowNetINR(row))}</td>
												<td>
													{row.isPartialPayment ? <PartialPaymentBadge /> : "—"}
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					</>
				)}
			</div>
		</Dialog>
	);
};

const CashFlowCard = ({ cashFlow }) => {
	const [moneyInOpen, setMoneyInOpen] = useState(false);
	const [moneyOutOpen, setMoneyOutOpen] = useState(false);

	if (!cashFlow) return null;

	const received = cashFlow.receivedINR || {};
	const receivedBreakdown = cashFlow.receivedBreakdown || {};
	const withdrawals = cashFlow.withdrawalsINR || {};
	const receivedDetails = cashFlow.receivedDetails || [];
	const withdrawalDetails = cashFlow.withdrawalDetails || {};
	const upiDetail = withdrawalDetails.upi || {};
	const voucherDetail = withdrawalDetails.vouchers || {};
	const upiTotal = upiDetail.total || {};
	const voucherTotal = voucherDetail.total || {};
	const upiPublishers = Array.isArray(upiDetail.byPublisher) ? upiDetail.byPublisher : [];
	const voucherPublishers = Array.isArray(voucherDetail.byPublisher) ? voucherDetail.byPublisher : [];
	const usdBreakdown = receivedBreakdown?.usd || {};
	const inrBreakdown = receivedBreakdown?.inr || {};

	const hasWithdrawalDetails =
		upiTotal.valueINR != null ||
		upiDetail.valueINR != null ||
		upiPublishers.length > 0 ||
		voucherTotal.valueINR != null ||
		voucherPublishers.length > 0 ||
		(withdrawals.total ?? 0) > 0;

	const grossInvoicedINR =
		receivedBreakdown.grossInvoicedINR ?? inrBreakdown.grossINR ?? inrBreakdown.grossSentINR ?? 0;
	const grossInvoicedUSD =
		receivedBreakdown.grossInvoicedUSD ?? usdBreakdown.grossUSD ?? 0;
	const gstINR = receivedBreakdown.gstAmountINR ?? inrBreakdown.gstINR ?? 0;
	const transferChargesUSD =
		receivedBreakdown.transferChargesUSD ?? usdBreakdown.transferChargesUSD ?? 0;
	const tdsINR = receivedBreakdown.tdsAmountINR ?? inrBreakdown.tdsINR ?? 0;
	const receivedFromINR =
		received.fromINRPartners ?? inrBreakdown.netReceivedINR ?? 0;
	const receivedFromUSD =
		received.fromUSDPartners ?? usdBreakdown.netReceivedINR ?? 0;
	// Indian partners (INR) + USD partners converted to INR
	const receivedInBank = Number((receivedFromINR + receivedFromUSD).toFixed(2));
	const moneyOutTotal = withdrawals.total ?? 0;
	const netCashPosition =
		cashFlow.netCashINR != null
			? cashFlow.netCashINR
			: Number((receivedInBank - moneyOutTotal).toFixed(2));
	const netPositive = netCashPosition >= 0;

	return (
		<>
			<div className="rs-cashflow-section-card">
				<div className="rs-cashflow-section-header">
					<div>
						<h3 className="rs-cashflow-section-heading">Cash Flow</h3>
						<p className="rs-cashflow-section-sub">
							Gross → GST / charges → TDS → bank → withdrawals → net
						</p>
					</div>
					<div className="rs-cashflow-section-actions">
						{receivedDetails.length > 0 && (
							<button
								type="button"
								onClick={() => setMoneyInOpen(true)}
								className="rs-cashflow-btn rs-cashflow-btn--in"
							>
								Payments ({receivedDetails.length})
							</button>
						)}
						{hasWithdrawalDetails && (
							<button
								type="button"
								onClick={() => setMoneyOutOpen(true)}
								className="rs-cashflow-btn rs-cashflow-btn--out"
							>
								Withdrawals
							</button>
						)}
					</div>
				</div>

				<div className="rs-cashflow-pipeline">
					<div className="rs-cashflow-step rs-cashflow-step--gross">
						<span className="rs-cashflow-step-index">01</span>
						<div className="rs-cashflow-step-label">Gross Amount</div>
						<div className="rs-cashflow-step-rows">
							<div className="rs-cashflow-step-row">
								<span>Indian</span>
								<strong className="rs-calc-num">{formatINR(grossInvoicedINR)}</strong>
							</div>
							<div className="rs-cashflow-step-row">
								<span>USD</span>
								<strong className="rs-calc-num">{formatUSD(grossInvoicedUSD)}</strong>
							</div>
						</div>
					</div>

					<div className="rs-cashflow-step rs-cashflow-step--gst">
						<span className="rs-cashflow-step-index">02</span>
						<div className="rs-cashflow-step-label">GST / Bank Charges</div>
						<div className="rs-cashflow-step-rows">
							<div className="rs-cashflow-step-row">
								<span>GST</span>
								<strong className="rs-calc-num">{formatINR(gstINR)}</strong>
							</div>
							<div className="rs-cashflow-step-row">
								<span>Charges</span>
								<strong className="rs-calc-num">{formatUSD(transferChargesUSD)}</strong>
							</div>
						</div>
					</div>

					<div className="rs-cashflow-step rs-cashflow-step--tds">
						<span className="rs-cashflow-step-index">03</span>
						<div className="rs-cashflow-step-label">TDS</div>
						<div className="rs-cashflow-step-value rs-calc-num">{formatINR(tdsINR)}</div>
						<div className="rs-cashflow-step-meta">INR partners</div>
					</div>

					<div className="rs-cashflow-step rs-cashflow-step--in">
						<span className="rs-cashflow-step-index">04</span>
						<div className="rs-cashflow-step-label">Received in Bank</div>
						<div className="rs-cashflow-step-value rs-calc-num">{formatINR(receivedInBank)}</div>
						<div className="rs-cashflow-step-rows">
							<div className="rs-cashflow-step-row">
								<span>Indian</span>
								<strong className="rs-calc-num">{formatINR(receivedFromINR)}</strong>
							</div>
							<div className="rs-cashflow-step-row">
								<span>USD → INR</span>
								<strong className="rs-calc-num">{formatINR(receivedFromUSD)}</strong>
							</div>
						</div>
					</div>

					<div className="rs-cashflow-step rs-cashflow-step--out">
						<span className="rs-cashflow-step-index">05</span>
						<div className="rs-cashflow-step-label">Withdrawals</div>
						<div className="rs-cashflow-step-value rs-calc-num">−{formatINR(moneyOutTotal)}</div>
						<div className="rs-cashflow-step-rows">
							<div className="rs-cashflow-step-row">
								<span>UPI</span>
								<strong className="rs-calc-num">{formatINR(withdrawals.upi)}</strong>
							</div>
							<div className="rs-cashflow-step-row">
								<span>Vouchers</span>
								<strong className="rs-calc-num">{formatINR(withdrawals.vouchers)}</strong>
							</div>
						</div>
					</div>

					<div className={`rs-cashflow-step rs-cashflow-step--net ${netPositive ? "" : "rs-cashflow-step--net-neg"}`}>
						<span className="rs-cashflow-step-index">06</span>
						<div className="rs-cashflow-step-label">Net Cash</div>
						<div className="rs-cashflow-step-value rs-cashflow-step-value--xl rs-calc-num">
							{formatINR(netCashPosition)}
						</div>
						<div className="rs-cashflow-step-meta">
							{netPositive ? "Surplus this period" : "Deficit this period"}
						</div>
					</div>
				</div>
			</div>

			<MoneyInDetailsModal
				open={moneyInOpen}
				onClose={() => setMoneyInOpen(false)}
				receivedDetails={receivedDetails}
				receivedBreakdown={receivedBreakdown}
				received={received}
			/>

			<MoneyOutDetailsModal
				open={moneyOutOpen}
				onClose={() => setMoneyOutOpen(false)}
				withdrawalDetails={withdrawalDetails}
				withdrawals={withdrawals}
			/>
		</>
	);
};

export default CashFlowCard;
